import { SimpleButton } from "@kalyzee/kalyzee-ui";
import { QRCode } from "@kalyzee/kast-web-sdk/web-components";
import React, { useCallback, useMemo, useState } from "react";
import { toastError, toastSuccess } from "../../helpers/toast";
import styles from "./MeetingShareContent.module.css";

export interface MeetingShareContentProps {
  /** Short id of the meeting to share. Nothing is rendered without it. */
  shortId?: string;
  className?: string;
}

/** Public join URL of a meeting, derived from where the app is currently served. */
export const getMeetingShareUrl = (shortId: string) => {
  const url = new URL(window.location.href);
  url.pathname = `/j/${shortId}`;
  url.search = "";
  url.hash = "";
  return url.toString();
};

/**
 * The QR code, the invitation link and the ways to pass it along. Shared by the in-meeting share
 * work box and the share popup of the meetings list.
 */
export const MeetingShareContent: React.FC<MeetingShareContentProps> = ({ shortId, className }) => {
  const [canShare] = useState(() => typeof navigator !== "undefined" && !!navigator.share);

  const shareUrl = useMemo(() => (shortId ? getMeetingShareUrl(shortId) : undefined), [shortId]);

  const onCopy = useCallback(() => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl).then(() => {
      toastSuccess("Lien copié dans le presse-papier");
    }).catch(err => {
      toastError("Impossible de copier le lien dans le presse-papier");
      console.error("Failed to copy share URL: ", err);
    });
  }, [shareUrl]);

  const onShare = useCallback(() => {
    if (!shareUrl) return;
    navigator.share({ title: "Rejoindre le meeting", url: shareUrl }).catch(err => {
      // The user cancelling the share sheet rejects the promise — ignore it.
      if (err?.name !== "AbortError") console.error("Failed to share URL: ", err);
    });
  }, [shareUrl]);

  if (!shareUrl) return null;

  return (
    <div className={className ?? styles.body}>
      <div className={styles.qrCard}>
        <QRCode text={shareUrl} className={styles.qr} />
      </div>

      <span className={styles.label}>Lien d'invitation</span>

      <div className={styles.linkRow}>
        <span className={styles.linkText} title={shareUrl}>{shareUrl}</span>
        <SimpleButton
          className={styles.copyBtn}
          onClick={onCopy}
          label="Copier"
        />
      </div>

      {canShare && (
        <button type="button" className={styles.shareLink} onClick={onShare}>
          Partager via une autre application
        </button>
      )}
    </div>
  );
};
